/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-15 15:00:00
 *
 *******************************************************/
// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  SI_GET_BOOST_TEMPLATES_REQUEST,
  SI_GET_BOOST_TEMPLATES_SUCCESS,
  SI_GET_BOOST_TEMPLATES_FAILURE,
  SI_GET_TEMPLATE_INSTANCES_REQUEST,
  SI_GET_TEMPLATE_INSTANCES_SUCCESS,
  SI_GET_TEMPLATE_INSTANCES_FAILURE,
  SI_ADD_CREATED_TEMPLATE_INSTANCE,
  SI_GET_TEMPLATE_INSTANCE_REQUEST,
  SI_GET_TEMPLATE_INSTANCE_SUCCESS,
  SI_GET_TEMPLATE_INSTANCE_FAILURE,
  UPDATE_WIDGET_INSTANCE_TEMPLATE_REQUEST,
  UPDATE_WIDGET_INSTANCE_TEMPLATE_SUCCESS,
  UPDATE_WIDGET_INSTANCE_TEMPLATE_FAILURE,
  SI_CLONE_BOOST_TEMPLATES_REQUEST,
  SI_CLONE_BOOST_TEMPLATES_SUCCESS,
  SI_CLONE_BOOST_TEMPLATES_FAILURE,
  SI_RENAME_TEMPLATES_REQUEST,
  SI_RENAME_TEMPLATES_SUCCESS,
  SI_RENAME_TEMPLATES_FAILURE,
  SI_REMOVE_TEMPLATE_REQUEST,
  SI_REMOVE_TEMPLATE_SUCCESS,
  SI_REMOVE_TEMPLATE_FAILURE,
  SI_DUPLICATE_TEMPLATE_REQUEST,
  SI_DUPLICATE_TEMPLATE_SUCCESS,
  SI_DUPLICATE_TEMPLATE_FAILURE,
  SI_CLEAN_STATE_EXIT_MANAGE_TEMPLATE_PAGE,
  UPDATE_FIELD,
  UPDATE_MULTI_FIELDS,
  SI_UPDATE_THEME_IDS_OF_TEMPLATE,
  SI_GET_WIDGET_VERSIONS_REQUEST,
  SI_GET_WIDGET_VERSIONS_SUCCESS,
  SI_GET_WIDGET_VERSIONS_FAILURE,
  SI_GET_WIDGET_VERSIONS_CONTENT_REQUEST,
  SI_GET_WIDGET_VERSIONS_CONTENT_SUCCESS,
  SI_GET_WIDGET_VERSIONS_CONTENT_FAILURE,
} from 'stores/flow-type/shopify-integration/template-instance.type';

/**
 * call to get list of all boost template instance
 * @return {object} The RSAA object for API middleware
 */
export const getBoostTemplateInstances = (i18n: I18n) => {
  return apiService.sendRequest(
    'shopify-integration.template-instance.get-boost-template-instances',
    {},
    [
      SI_GET_BOOST_TEMPLATES_REQUEST,
      {
        type: SI_GET_BOOST_TEMPLATES_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_GET_BOOST_TEMPLATES_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get list of template instance for specific store
 * @return {object} The RSAA object for API middleware
 */
export const getTemplateInstances = (i18n: I18n) => {
  return apiService.sendRequest(
    'shopify-integration.template-instance.get-template-instances',
    {},
    [
      SI_GET_TEMPLATE_INSTANCES_REQUEST,
      {
        type: SI_GET_TEMPLATE_INSTANCES_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_GET_TEMPLATE_INSTANCES_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to add created template instance
 */
export const addCreatedTemplateInstance = (templateInstance, isBoostTemplate) => {
  return {
    type: SI_ADD_CREATED_TEMPLATE_INSTANCE,
    payload: { data: templateInstance, isBoostTemplate }
  };
};

export const getTemplateInstanceById = (i18n: I18n, templateId, isNested: boolean) => {
  return apiService.sendRequest(
    'shopify-integration.template-instance.get-template-instance',
    { params: { templateId }, queries: { elementTemplates: `group${isNested ? ',nested' : ''}` } },
    [
      SI_GET_TEMPLATE_INSTANCE_REQUEST,
      {
        type: SI_GET_TEMPLATE_INSTANCE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_GET_TEMPLATE_INSTANCE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};
/**
 * call to clone boost template instance
 * @return {object} The RSAA object for API middleware
 */
export const cloneBoostTemplateInstances = (i18n: I18n, themeNameLib: String) => {
  const params = { themeNameLib };
  return apiService.sendRequest(
    'shopify-integration.template-instance.clone-boost-template-instances',
    { params },
    [
      SI_CLONE_BOOST_TEMPLATES_REQUEST,
      {
        type: SI_CLONE_BOOST_TEMPLATES_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_CLONE_BOOST_TEMPLATES_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

export const updateTemplateInstancesWidgets = (
  i18n: I18n,
  templateId,
  widgets,
) => {
  return apiService.sendRequest(
    'shopify-integration.template-instance.update-template-instance-widgets',
    { params: { templateId }, body: { templateId, editedWidgets: widgets, } },
    [
      UPDATE_WIDGET_INSTANCE_TEMPLATE_REQUEST,
      {
        type: UPDATE_WIDGET_INSTANCE_TEMPLATE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          return { data, editedWidget: widgets[0] };
        }
      },
      {
        type: UPDATE_WIDGET_INSTANCE_TEMPLATE_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate(
              'AdminHTML.ShopifyIntegrationSettings.Actions.updateWidgetInstanceById_Failure'
            ),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

/**
 * call to rename template instance
 * @return {object} The RSAA object for API middleware
 */
export const renameTemplateInstance = (i18n: I18n, templateId: String, templateName: String) => {
  const params = { templateId };
  const body = { templateName };
  return apiService.sendRequest(
    'shopify-integration.template-instance.rename-template-instance',
    { params, body },
    [
      SI_RENAME_TEMPLATES_REQUEST,
      {
        type: SI_RENAME_TEMPLATES_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_RENAME_TEMPLATES_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to remove template instance
 * @return {object} The RSAA object for API middleware
 */
export const removeTemplateInstance = (i18n: I18n, templateId: String) => {
  const params = { templateId };
  return apiService.sendRequest(
    'shopify-integration.template-instance.remove-template-instance',
    { params },
    [
      SI_REMOVE_TEMPLATE_REQUEST,
      {
        type: SI_REMOVE_TEMPLATE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, templateId };
        }
      },
      {
        type: SI_REMOVE_TEMPLATE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to duplicate template instance
 * @return {object} The RSAA object for API middleware
 */
export const duplicateTemplateInstance = (i18n: I18n, templateId: String) => {
  const params = { templateId };
  return apiService.sendRequest(
    'shopify-integration.template-instance.duplicate-template-instance',
    { params },
    [
      SI_DUPLICATE_TEMPLATE_REQUEST,
      {
        type: SI_DUPLICATE_TEMPLATE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_DUPLICATE_TEMPLATE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to add created template instance
 */
export const cleanStateExitManageTemplatePage = () => {
  return {
    type: SI_CLEAN_STATE_EXIT_MANAGE_TEMPLATE_PAGE,
    payload: { data: {} }
  };
};

/**
 * Call to update field in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateField = (fieldName: string, data: any) => {
  return { type: UPDATE_FIELD, payload: { fieldName, data } };
};

/**
 * Call to update multi fields in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateMultiFields = (data: any) => {
  return { type: UPDATE_MULTI_FIELDS, payload: { data } };
};

/**
 * Call to update multi fields in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateThemeIdsOfTemplate = (
  fromTemplateId: string,
  toTemplateId: string,
  themeId: number
) => {
  return {
    type: SI_UPDATE_THEME_IDS_OF_TEMPLATE,
    payload: { fromTemplateId, toTemplateId, themeId }
  };
};

export const getWidgetVersions = (i18n: I18n, templateId, widgetId) => {
  return apiService.sendRequest(
    'shopify-integration.template-instance.template-instance-versions',
    { params: { templateId }, queries: { widgetId } },
    [
      SI_GET_WIDGET_VERSIONS_REQUEST,
      {
        type: SI_GET_WIDGET_VERSIONS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, widgetId };
        }
      },
      {
        type: SI_GET_WIDGET_VERSIONS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ShopifyIntegration.fetchAPI.getWidgetVersions_Fail'),
            i18n
          )
      }
    ]
  );
};

export const getWidgetVersionsContent = (i18n: I18n, templateId, widgetId, versionId) => {
  return apiService.sendRequest(
    'shopify-integration.template-instance.template-instance-versions-content',
    { params: { templateId }, queries: { widgetId, versionId } },
    [
      SI_GET_WIDGET_VERSIONS_CONTENT_REQUEST,
      {
        type: SI_GET_WIDGET_VERSIONS_CONTENT_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_GET_WIDGET_VERSIONS_CONTENT_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ShopifyIntegration.fetchAPI.getWidgetVersions_Fail'),
            i18n
          )
      }
    ]
  );
};


